import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButton, PrismCard } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import sortableGroup from 'ember-sortable/modifiers/sortable-group';
import sortableItem from 'ember-sortable/modifiers/sortable-item';
import GeneralDropdownsDropdownYield, {
  type Dropdown,
} from 'lumen/components/general/dropdowns/dropdown-yield';
import GeneralDropdownsTriggersTriggerDropdownIconButton from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-icon-button';
import DragItem from 'lumen/components/hotel/net-revenue/cost-drilldown-section/table-layout/drag-item';
import type { CostColumnKey } from 'lumen/utils/net-revenue/cost-columns';
import { localCopy } from 'tracked-toolbox';

import styles from './table-layout.module.css';

const COLUMN_LABEL_KEYS: Record<CostColumnKey, string> = {
  adr: 'general.adr',
  avgCost: 'net_revenue.avg_cost',
  cost: 'net_revenue.cost',
  costPercent: 'net_revenue.cost_percent',
  netAdr: 'net_revenue.net_adr',
  netRevenue: 'net_revenue.net_revenue',
  netRevenuePercent: 'net_revenue.net_revenue_percent',
  roomNights: 'general.room_nights',
  roomRevenue: 'net_revenue.room_revenue',
};

interface HotelNetRevenueCostDrilldownSectionTableLayoutSignature {
  Args: {
    columnOrder: CostColumnKey[];
    hiddenColumns: CostColumnKey[];
    nameColumnLabel: string;
    onConfirm: (next: {
      columnOrder: CostColumnKey[];
      hiddenColumns: CostColumnKey[];
    }) => void;
  };
}

export default class HotelNetRevenueCostDrilldownSectionTableLayout extends Component<HotelNetRevenueCostDrilldownSectionTableLayoutSignature> {
  @service declare intl: Services['intl'];

  @localCopy('args.hiddenColumns') declare draftHidden: CostColumnKey[];
  @localCopy('args.columnOrder') declare draftOrder: CostColumnKey[];

  /**
   * The order shown in the popover — visible keys first (preserving
   * the user's order), then hidden keys at the bottom. The committed
   * `columnOrder` follows this same shape so re-opening reflects what
   * the user saw on Apply.
   */
  get displayedOrder(): CostColumnKey[] {
    const hidden = new Set(this.draftHidden);
    const visible = this.draftOrder.filter((key) => !hidden.has(key));
    const hiddenList = this.draftOrder.filter((key) => hidden.has(key));
    return [...visible, ...hiddenList];
  }

  @action
  isHidden(key: CostColumnKey): boolean {
    return this.draftHidden.includes(key);
  }

  @action
  labelFor(key: CostColumnKey): string {
    return this.intl.t(COLUMN_LABEL_KEYS[key]);
  }

  @action
  onApply(dropdown: Dropdown): void {
    this.args.onConfirm({
      columnOrder: this.displayedOrder,
      hiddenColumns: this.draftHidden,
    });
    dropdown.toggleDropdown();
  }

  @action
  onCancel(dropdown?: Dropdown): void {
    this.draftOrder = this.args.columnOrder;
    this.draftHidden = this.args.hiddenColumns;
    dropdown?.toggleDropdown();
  }

  @action
  onOrderChange(items: CostColumnKey[]): void {
    this.draftOrder = items;
  }

  @action
  toggleVisible(key: CostColumnKey): void {
    this.draftHidden = this.draftHidden.includes(key)
      ? this.draftHidden.filter((k) => k !== key)
      : [...this.draftHidden, key];
  }

  <template>
    <div class="relative">
      {{#let
        (component
          GeneralDropdownsTriggersTriggerDropdownIconButton
          buttonType="secondary"
          dataTestId="cost-table-layout-trigger"
          iconBefore="sliders"
        )
        as |triggerComponent|
      }}
        <GeneralDropdownsDropdownYield
          @dropdownClass=".cost-table-layout-dropdown"
          {{! @glint-expect-error: dropdownClassName is untyped on the wrapper }}
          @dropdownClassName="cost-table-layout-dropdown top-[40px] right-0"
          @label={{t "net_revenue.table_layout"}}
          @onCancel={{this.onCancel}}
          @selected={{null}}
          @triggerComponent={{triggerComponent}}
          as |dropdown|
        >
          <PrismCard @contentLayout="noPadding" class={{styles.card}}>
            <:content>
              <div class="flex flex-col gap-200 p-500">
                <p
                  class="text-200--medium"
                  data-test-id="cost-table-layout-title"
                >
                  {{t "net_revenue.columns"}}
                </p>

                {{#let "cost-table-layout-columns" as |groupName|}}
                  <ul
                    class="text-300--regular {{styles.list}}"
                    data-test-id="cost-table-layout-list"
                    {{sortableGroup
                      onChange=this.onOrderChange
                      groupName=groupName
                    }}
                  >
                    <li
                      class={{styles.locked-row}}
                      data-test-id="cost-table-layout-locked-row"
                    >
                      <span class="flex-1 truncate">
                        {{@nameColumnLabel}}
                      </span>
                    </li>
                    {{#each this.displayedOrder key="@identity" as |key|}}
                      <DragItem
                        @isHidden={{this.isHidden key}}
                        @label={{this.labelFor key}}
                        @toggleVisible={{fn this.toggleVisible key}}
                        {{sortableItem
                          model=key
                          distance=5
                          groupName=groupName
                        }}
                      />
                    {{/each}}
                  </ul>
                {{/let}}
              </div>
            </:content>
            <:footer>
              <div class="flex flex-row justify-end gap-200">
                <PrismButton
                  @buttonType="secondary"
                  @label={{t "general.cancel"}}
                  data-test-id="cost-table-layout-cancel"
                  {{on "click" (fn this.onCancel dropdown)}}
                />
                <PrismButton
                  @label={{t "general.apply"}}
                  data-test-id="cost-table-layout-apply"
                  {{on "click" (fn this.onApply dropdown)}}
                />
              </div>
            </:footer>
          </PrismCard>
        </GeneralDropdownsDropdownYield>
      {{/let}}
    </div>
  </template>
}
